import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/admin';
import { Ticket, AlertCircle, Check, Loader, Trash2, Search, Gift } from 'lucide-react';

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminPromoCode = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [form, setForm] = useState({
    code: '',
    bonusPercent: '10',
    expiresAt: ''
  });
  const [bonusGrant, setBonusGrant] = useState({
    usernameSearch: '',
    amount: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [grantingBonus, setGrantingBonus] = useState(false);

  const loadPromo = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getPromoCode();
      const promo = response?.promoCode;
      if (promo) {
        setForm({
          code: promo.code || '',
          bonusPercent: String(promo.bonus_percent ?? promo.bonusPercent ?? ''),
          expiresAt: toDatetimeLocal(promo.expires_at ?? promo.expiresAt)
        });
      }
    } catch (err) {
      showMessage('error', err.message || 'Failed to load promo code');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromo();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await adminApi.savePromoCode({
        code: form.code.trim(),
        bonusPercent: parseFloat(form.bonusPercent),
        expiresAt: new Date(form.expiresAt).toISOString()
      });
      if (response?.success) {
        showMessage('success', 'Promo code saved');
        const promo = response.promoCode;
        if (promo) {
          setForm({
            code: promo.code || form.code,
            bonusPercent: String(promo.bonus_percent ?? form.bonusPercent),
            expiresAt: toDatetimeLocal(promo.expires_at ?? form.expiresAt)
          });
        }
      } else {
        showMessage('error', response?.error || 'Save failed');
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const searchUsers = async () => {
    const query = bonusGrant.usernameSearch.trim();
    if (query.length < 2) {
      setUserResults([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const response = await adminApi.getUsers({ search: query }, 10, 0);
      if (response?.success) {
        setUserResults(response.users || []);
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Ricerca utenti fallita');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleGrantBonus = async (e) => {
    e.preventDefault();
    if (!selectedUser?.id) {
      showMessage('error', 'Seleziona un utente dalla ricerca');
      return;
    }
    const amount = parseFloat(bonusGrant.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showMessage('error', 'Inserisci un importo bonus valido');
      return;
    }
    if (!window.confirm(`Assegnare ${amount} Bonus a ${selectedUser.username}?`)) {
      return;
    }

    setGrantingBonus(true);
    try {
      const response = await adminApi.grantUserBonus(selectedUser.id, amount);
      if (response?.success) {
        showMessage(
          'success',
          `Assegnati ${response.bonusGranted} Bonus a ${response.username}. Nuovo saldo bonus: ${response.newBonusBalance}`
        );
        setBonusGrant({ usernameSearch: '', amount: '' });
        setSelectedUser(null);
        setUserResults([]);
      } else {
        showMessage('error', response?.error || 'Assegnazione bonus fallita');
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Assegnazione bonus fallita');
    } finally {
      setGrantingBonus(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Disattivare il promo code attivo?')) return;
    setSaving(true);
    try {
      const response = await adminApi.deactivatePromoCode();
      if (response?.success) {
        showMessage('success', 'Promo code disattivato');
        setForm({ code: '', bonusPercent: '10', expiresAt: '' });
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Deactivate failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Ticket className="w-8 h-8 mr-3 text-amber-400" />
          Promo Code
        </h1>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-900/50 border-green-700 text-green-200'
            : 'bg-red-900/50 border-red-700 text-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? <Check className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <p className="text-gray-400 text-sm mb-6">
          Imposta un codice promo attivo. Gli utenti lo inseriscono in deposito e ricevono una percentuale extra in <strong className="text-amber-300">Bonus</strong> (usabile solo per aste e pack).
        </p>

        {loading ? (
          <div className="text-center py-8">
            <Loader className="w-8 h-8 animate-spin mx-auto text-indigo-400" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Codice</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white font-mono uppercase"
                placeholder="es. WELCOME10"
                required
                minLength={3}
                maxLength={32}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">% Bonus sul deposito</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                value={form.bonusPercent}
                onChange={(e) => setForm({ ...form, bonusPercent: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Scadenza</label>
              <input
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
                required
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {saving ? 'Salvataggio...' : 'Salva promo'}
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={saving || !form.code}
                className="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Disattiva
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-1 flex items-center">
          <Gift className="w-5 h-5 mr-2 text-amber-400" />
          Assegna Bonus manualmente
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          Cerca un utente per username e accredita un importo in <strong className="text-amber-300">Bonus</strong> (usabile solo per aste e pack, non prelevabile).
        </p>

        <form onSubmit={handleGrantBonus} className="space-y-5">
          <div className="relative">
            <label className="block text-sm text-gray-400 mb-1">Username</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={bonusGrant.usernameSearch}
                onChange={(e) => {
                  setBonusGrant((prev) => ({ ...prev, usernameSearch: e.target.value }));
                  setSelectedUser(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    searchUsers();
                  }
                }}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
                placeholder="Cerca username..."
              />
            </div>
            {userSearchLoading && (
              <p className="text-xs text-gray-500 mt-1">Ricerca...</p>
            )}
            {userResults.length > 0 && !selectedUser && (
              <ul className="absolute z-10 mt-1 w-full bg-gray-900 border border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {userResults.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUser(u);
                        setBonusGrant((prev) => ({ ...prev, usernameSearch: u.username }));
                        setUserResults([]);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700"
                    >
                      {u.username}
                      <span className="text-gray-500 ml-2 text-xs">{u.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {selectedUser && (
              <p className="text-xs text-indigo-300 mt-1">
                Selezionato: {selectedUser.username}
                {selectedUser.bonus_balance != null && (
                  <span className="text-gray-400 ml-2">
                    (bonus attuale: {parseFloat(selectedUser.bonus_balance || 0).toFixed(2)})
                  </span>
                )}
              </p>
            )}
            <button
              type="button"
              onClick={searchUsers}
              className="mt-2 px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
            >
              Cerca
            </button>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Importo Bonus (DIOS)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="100000"
              value={bonusGrant.amount}
              onChange={(e) => setBonusGrant((prev) => ({ ...prev, amount: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
              placeholder="es. 50"
              required
            />
          </div>

          <button
            type="submit"
            disabled={grantingBonus || !selectedUser}
            className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg disabled:opacity-50"
          >
            {grantingBonus ? 'Assegnazione...' : 'Assegna Bonus'}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
};

export default AdminPromoCode;
